import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Anchor } from '@radix-ui/react-popover';

import { FauxInlineEditArea, InlineEditArea } from 'ui/inlineEdit';
import { PopoverAlignment } from 'ui/popover';
import { AIQuerySummary } from 'ui/queryBuilder';
import { spacing } from 'ui/theme';

import { ListDescriptionAIEmptyTrigger } from './ListDescriptionAIEmptyTrigger';
import { useListDetailsContext } from '../../contexts/listDetails';
import { usePermissionsContext } from '../../contexts/permissions';
import { useSegmentationQueryContext } from '../../contexts/segmentationQuery';
import {
  useListId,
  useSuggestedDescription,
  useTranslation,
  useUpdateListDescriptionMutation,
} from '../../hooks';
import { PAGE_VARIATIONS } from '../../constants';

export const DescriptionWrapper = styled.div`
  margin-bottom: ${spacing.quarter};
`;

export const testIds = {
  listDescription: 'list-description',
};

export const ListDescriptionSection = () => {
  const { t } = useTranslation();
  const { canManageSegmentation, canUseSegmentationAiDescriptions } = usePermissionsContext();
  const { list, isLoading, pageVariation } = useListDetailsContext();
  const isDynamicListView = pageVariation === PAGE_VARIATIONS.DYNAMIC;
  const listId = useListId();
  const { updateListDescription } = useUpdateListDescriptionMutation(listId);
  const { editableQuery } = useSegmentationQueryContext();
  const props = useSuggestedDescription({ listId, query: editableQuery }); // { description, error, fetchDescription, isLoading }
  const [description, setDescription] = useState(list.description || '');
  const descriptionInput = useRef<HTMLTextAreaElement>(null);

  const handleBlur = () => {
    const stateDescription = description.trim();
    const listDescription = list.description || ''; // may be undefined for imported lists (or old lists)
    if (stateDescription !== listDescription) {
      updateListDescription(stateDescription);
    }
  };

  const onCancelAIQuerySummary = () => {
    // set focus on input (subsequent blur will trigger save)
    descriptionInput.current?.focus();
  };

  const onCommitAIQuerySummary = (desc: string) => {
    setDescription(desc);
    // set focus on input (subsequent blur will trigger save)
    descriptionInput.current?.focus();
  };

  useEffect(() => {
    if (!isLoading) {
      setDescription(list.description || '');
    }
  }, [isLoading, list]);

  return canManageSegmentation ? (
    <DescriptionWrapper data-test={testIds.listDescription}>
      {isDynamicListView && canUseSegmentationAiDescriptions ? (
        <AIQuerySummary
          align={PopoverAlignment.Left}
          isReadOnly={!canManageSegmentation || !listId}
          onClosePopover={onCancelAIQuerySummary}
          onCommit={onCommitAIQuerySummary}
          onInvalidQuery={onCancelAIQuerySummary}
          {...props}
        >
          <Anchor>
            <InlineEditArea
              EmptyTrigger={ListDescriptionAIEmptyTrigger}
              ref={descriptionInput}
              maxLength={255}
              onBlur={handleBlur}
              onChange={setDescription}
              placeholder={t('header.emptyDescription')}
              showFocused
              value={description ?? ''}
            />
          </Anchor>
        </AIQuerySummary>
      ) : (
        <InlineEditArea
          maxLength={255}
          onBlur={handleBlur}
          onChange={setDescription}
          placeholder={t('header.emptyDescription')}
          value={description ?? ''}
        />
      )}
    </DescriptionWrapper>
  ) : (
    <>
      {!!description ? (
        <DescriptionWrapper data-test={testIds.listDescription}>
          <FauxInlineEditArea value={description} />
        </DescriptionWrapper>
      ) : null}
    </>
  );
};
